import {useEffect, useMemo, useState} from "react";

import {
  Alert,
  Box,
  Snackbar,
  Stack,
} from "@mui/material";
import {useOutletContext, useParams} from "react-router-dom";

import {Category_File_Type, useMaterialQuery} from "@generated/graphql";
import CircularLoading from "components/CircularLoading";
import SearchInput from "components/SearchInput";
import BackButton from "pages/Material/view/components/BackButton";
import {MaterialContextProps} from "pages/Material/types";

import MaterialFilesContent from "./components/MaterialFilesContent";
import {
  getCategoryBannerUrl,
  getFolderLink,
  scrollToCurrentHashElement
} from "./helpers";
import useFolderExport from "./hooks/useFolderExport";
import useMaterialFirstViewFiles from "./hooks/useMaterialFirstViewFiles";
import type {Notification} from "./types";

const MaterialFirstView = () => {
  const {setSelectedSubcategory, setSelectedCategory, setMixedFilterValue} = useOutletContext<MaterialContextProps>()
  const {id} = useParams()
  const materialId = id!;
  const [notification, setNotification] = useState<Notification | null>(null);
  const {data, loading: materialLoading} = useMaterialQuery({
    variables: {
      materialId
    }
  });
  const {
    searchInput,
    handleSearchInputChange,
    materialFiles,
    recommendationFiles,
    otherFileTree,
    isSearchActive,
    loading: materialFilesLoading,
  } = useMaterialFirstViewFiles(materialId);

  const material = data?.material;
  const categoryFiles = useMemo(() => material?.subcategory?.category.files ?? [], [material?.subcategory?.category.files]);
  const desktopBannerUrl = getCategoryBannerUrl(categoryFiles, Category_File_Type.BannerDesktop);
  const mobileBannerUrl = getCategoryBannerUrl(categoryFiles, Category_File_Type.BannerMobile);
  const bannerUrl = desktopBannerUrl || mobileBannerUrl;
  const {activeExportPath, handleExportFolder} = useFolderExport(material?.subcategory?.id, setNotification);

  const handleCopyFolderLink = (path: string) => {
    navigator.clipboard.writeText(getFolderLink(path))
      .then(() => setNotification({message: 'Ссылка скопирована', severity: 'success'}))
      .catch(() => setNotification({message: 'Не удалось скопировать ссылку', severity: 'error'}));
  };

  useEffect(() => {
    if (materialLoading || isSearchActive || otherFileTree.length === 0) {
      return;
    }

    const timeoutId = window.setTimeout(scrollToCurrentHashElement, 100);

    return () => window.clearTimeout(timeoutId);
  }, [materialLoading, isSearchActive, otherFileTree]);

  useEffect(() => {
    if (material) {
      setSelectedCategory(material.subcategory?.category || null);
      setSelectedSubcategory(material.subcategory?.id || '');
      setMixedFilterValue(material.subcategory || null)
    }
  }, [material, setMixedFilterValue, setSelectedCategory, setSelectedSubcategory]);

  if (materialLoading) return <CircularLoading/>
  return (
    <Box>
      <BackButton sx={{mb: 4}}/>
      <SearchInput inputValue={searchInput} setInputValue={handleSearchInputChange}/>
      {bannerUrl && (
        <Box
          component="picture"
          sx={{
            display: 'block',
            width: '100%',
            mb: 3,
          }}
        >
          {mobileBannerUrl && (
            <source
              srcSet={mobileBannerUrl}
              media="(max-width: 767px)"
            />
          )}
          <Box
            component="img"
            src={bannerUrl}
            alt=""
            sx={{
              display: 'block',
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </Box>
      )}
      <Stack spacing={3} px={3}>
        <MaterialFilesContent
          loading={materialFilesLoading}
          isSearchActive={isSearchActive}
          materialFiles={materialFiles}
          recommendationFiles={recommendationFiles}
          otherFileTree={otherFileTree}
          activeExportPath={activeExportPath}
          onCopyFolderLink={handleCopyFolderLink}
          onExportFolder={handleExportFolder}
        />
      </Stack>
      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={3000}
        onClose={() => setNotification(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert
          severity={notification?.severity || 'success'}
          variant="filled"
          onClose={() => setNotification(null)}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default MaterialFirstView;
